import type { TableOfContentData } from '@/components/tiptap-editor/tiptap-extension/zy-table-of-contents-extension';
import { contentResourceGuidFromBlockAttrs } from '@/lib/get-content-resource-guid-at-viewport-top';
import type { Node as PMNode } from '@tiptap/pm/model';

export type HeadingOutlineItem = {
    id: string | null;
    title: string;
    level: number;
    pos: number;
    contentResourceGuid: string | null;
};

const ZY_VERSION_NODE_TYPES = new Set(['zyVersionBlock', 'zyVersionInline']);

function resolveSectionId(doc: PMNode, sectionId?: string | number | null): string | number | null | undefined {
    if (sectionId != null && sectionId !== '') {
        return sectionId;
    }
    const docSectionId = doc.attrs?.sectionId as string | number | null | undefined;
    if (docSectionId != null && docSectionId !== '') {
        return docSectionId;
    }
    return sectionId;
}

function findHeadingPos(doc: PMNode, headingPos: number, headingId?: string | null): number | null {
    try {
        const $pos = doc.resolve(headingPos);
        for (let depth = $pos.depth; depth >= 1; depth--) {
            if ($pos.node(depth).type.name === 'heading') {
                return headingPos;
            }
        }
        if ($pos.nodeAfter?.type.name === 'heading') {
            return headingPos;
        }
    } catch {
        // Fall through to id lookup.
    }

    if (typeof headingId !== 'string' || headingId.length === 0) {
        return null;
    }

    let foundPos: number | null = null;
    doc.descendants((node, pos) => {
        if (foundPos !== null || node.type.name !== 'heading') {
            return;
        }
        if (node.attrs.id === headingId) {
            foundPos = pos;
        }
    });
    return foundPos;
}

/**
 * GUIDs for blocks that export as their own top-level content resources (after zyVersion unwrap).
 */
export function collectExportRootContentResourceGuids(doc: PMNode, sectionId?: string | number | null): Set<string> {
    const sectionIdVal = resolveSectionId(doc, sectionId);
    const guids = new Set<string>();

    const addNodeGuid = (node: PMNode) => {
        const guid = contentResourceGuidFromBlockAttrs(node.attrs as { guid?: string; id?: string }, sectionIdVal);
        if (guid) {
            guids.add(guid);
        }
    };

    doc.forEach(topNode => {
        if (ZY_VERSION_NODE_TYPES.has(topNode.type.name)) {
            topNode.forEach(child => addNodeGuid(child));
            return;
        }
        addNodeGuid(topNode);
    });

    return guids;
}

/**
 * Returns the preview-navigable content resource GUID for a heading, or null if the heading
 * does not export as its own top-level content resource.
 */
export function headingPreviewContentResourceGuid(
    doc: PMNode,
    headingPos: number,
    sectionId?: string | number | null,
    exportRootGuids?: Set<string>,
    headingAttrs?: { guid?: string; id?: string },
): string | null {
    const headingId = typeof headingAttrs?.id === 'string' ? headingAttrs.id : null;
    const resolvedPos = findHeadingPos(doc, headingPos, headingId);
    if (resolvedPos === null) {
        return null;
    }

    const sectionIdVal = resolveSectionId(doc, sectionId);
    const attrs =
        headingAttrs ??
        (() => {
            const $pos = doc.resolve(resolvedPos);
            for (let depth = $pos.depth; depth >= 1; depth--) {
                if ($pos.node(depth).type.name === 'heading') {
                    return $pos.node(depth).attrs as { guid?: string; id?: string };
                }
            }
            return $pos.nodeAfter?.attrs as { guid?: string; id?: string } | undefined;
        })();

    const guid = contentResourceGuidFromBlockAttrs(attrs, sectionIdVal);
    if (!guid) {
        return null;
    }

    const exportGuids = exportRootGuids ?? collectExportRootContentResourceGuids(doc, sectionIdVal);
    return exportGuids.has(guid) ? guid : null;
}

export function mapTableOfContentsToHeadingOutline(
    anchors: TableOfContentData,
    sectionId?: string | number | null,
    doc?: PMNode,
): HeadingOutlineItem[] {
    const sectionIdVal = doc ? resolveSectionId(doc, sectionId) : sectionId;
    const exportRootGuids = doc ? collectExportRootContentResourceGuids(doc, sectionIdVal) : null;

    return anchors.flatMap(anchor => {
        const nodeId = anchor.node.attrs?.id;
        const id = typeof nodeId === 'string' && nodeId.length > 0 ? nodeId : anchor.id || null;
        const contentResourceGuid = doc
            ? headingPreviewContentResourceGuid(doc, anchor.pos, sectionIdVal, exportRootGuids!, anchor.node.attrs)
            : contentResourceGuidFromBlockAttrs(anchor.node.attrs, sectionId);

        if (doc && !contentResourceGuid) {
            return [];
        }

        return [
            {
                id,
                title: anchor.textContent.trim() || '(untitled heading)',
                level: anchor.originalLevel,
                pos: anchor.pos,
                contentResourceGuid,
            },
        ];
    });
}

/**
 * Equality for outline UI / React context updates. Excludes `pos` so document edits that
 * only shift heading positions do not invalidate the snapshot.
 */
export function headingOutlineStructurallyEqual(a: HeadingOutlineItem[], b: HeadingOutlineItem[]): boolean {
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0; i < a.length; i++) {
        const left = a[i]!;
        const right = b[i]!;
        if (
            left.id !== right.id ||
            left.title !== right.title ||
            left.level !== right.level ||
            left.contentResourceGuid !== right.contentResourceGuid
        ) {
            return false;
        }
    }
    return true;
}

export function scrollToHeadingOutlineItem(
    heading: HeadingOutlineItem,
    previewMode: boolean,
    scrollToPos: (pos: number) => void,
    snapToGuid: (guid: string) => void,
): void {
    if (previewMode && heading.contentResourceGuid) {
        snapToGuid(heading.contentResourceGuid);
        return;
    }
    scrollToPos(heading.pos);
}
